import { useContext, useMemo } from 'react';
import { usePreventScroll } from 'react-aria';
import {
  ComboBoxStateContext as RACComboBoxStateContext,
  ListBox as RACListBox,
  ListLayout as RACListLayout,
  type ListLayoutOptions as RACListLayoutOptions,
  ListStateContext as RACListStateContext,
  Virtualizer as RACVirtualizer,
} from 'react-aria-components';
import type { CollectionItem, Key } from '../types.js';
import type { TagFieldProps } from './tag-field.js';

const VIRTUAL_LAYOUT_OPTIONS: RACListLayoutOptions = {
  estimatedRowHeight: 36,
  estimatedHeadingHeight: 32,
  padding: 0,
  gap: 0,
};

/** @internal */
export function TagFieldListBox<T extends CollectionItem>({
  children,
  inputId,
  selectedKeys,
  setSelectedKeys,
  items,
  onScroll,
  isVirtualized,
}: Pick<TagFieldProps<T>, 'children' | 'items' | 'onScroll' | 'isVirtualized'> & {
  inputId: string;
  selectedKeys: Set<Key>;
  setSelectedKeys: (keys: Set<Key>) => void;
}) {
  const comboBoxState = useContext(RACComboBoxStateContext);
  const listState = useContext(RACListStateContext);

  usePreventScroll({ isDisabled: !comboBoxState?.isOpen });

  // Override RACListStateContext to allow multiple selection within the list
  const updatedListState = useMemo(() => {
    if (!listState || !listState.selectionManager) {
      return listState;
    }

    // This is a temporary mutation before a context update, so this is fine
    // @ts-expect-error `selectionManager.state` is private
    // eslint-disable-next-line react-hooks/immutability
    listState.selectionManager.state.selectionMode = 'multiple';
    // @ts-expect-error `selectionManager.state` is private
    // eslint-disable-next-line react-hooks/immutability
    listState.selectionManager.state.selectedKeys = selectedKeys;
    // @ts-expect-error `selectionManager.state` is private
    // eslint-disable-next-line react-hooks/immutability
    listState.selectionManager.state.setSelectedKeys = (keys: Set<Key> | 'all') => {
      if (keys === 'all') {
        // When the end-user presses Ctrl/Cmd + A on a list which has `selectionMode` set to `'multiple'`,
        // the selection manager emits the `'all'` string as an indication to select all items.
        // We don't want to do that - instead, we select the contents of the input field.
        // Querying by ID because RACInputContext has a null ref for some reason.
        (document.getElementById(inputId) as HTMLInputElement | null)?.select();
      } else {
        setSelectedKeys(keys);
      }
    };
    // @ts-expect-error `selectionManager.state` is private
    // eslint-disable-next-line react-hooks/immutability
    listState.selectionManager.state.disallowEmptySelection = false;

    return {
      ...listState,
    };
  }, [listState, selectedKeys, setSelectedKeys, inputId]);

  const listBox = (
    <RACListBox className="cim-combo-box-listbox" onScroll={onScroll} items={items}>
      {children}
    </RACListBox>
  );

  return (
    <RACListStateContext.Provider value={updatedListState}>
      {isVirtualized ? (
        <RACVirtualizer layout={RACListLayout} layoutOptions={VIRTUAL_LAYOUT_OPTIONS}>
          {listBox}
        </RACVirtualizer>
      ) : (
        listBox
      )}
    </RACListStateContext.Provider>
  );
}
